<script setup lang="ts">
import { computed, ref } from 'vue'
import { useHead } from '@unhead/vue'
import type { Resolved } from '@/types'
import { PATHS } from '@/content'
import { useBlogStore, fetchBlogList, nonEmptyIn, type BlogList } from '@/lib/blog'
import { baseHead, breadcrumbLd } from '@/lib/seo'
import ArticleCard from '@/components/ArticleCard.vue'
import BreadcrumbBar from '@/components/BreadcrumbBar.vue'

const props = defineProps<{ resolved: Resolved }>()
const service = computed(() => props.resolved.service)

// Liste servie en direct : la premiere page arrive avec la page, les suivantes et le
// filtre par categorie sont demandes au serveur live (/__live/articles).
const blog = useBlogStore()
const cats = computed(() => nonEmptyIn(blog.payload?.categories || []))
const list = ref<BlogList>(
  blog.payload?.list || { items: [], total: 0, page: 1, perPage: 12, categoryId: null },
)
const loading = ref(false)
const activeCat = computed(() => list.value.categoryId)
const page = computed(() => list.value.page)
const total = computed(() => list.value.total)
const totalPages = computed(() => Math.max(1, Math.ceil(list.value.total / list.value.perPage)))
const paged = computed(() => list.value.items)

let seq = 0
async function load(p: number, categoryId: number | null) {
  const mine = ++seq
  loading.value = true
  try {
    const next = await fetchBlogList(p, categoryId)
    if (mine === seq) list.value = next
  } catch {
    /* liste courante conservee */
  } finally {
    if (mine === seq) loading.value = false
  }
}

function setCat(id: number | null) {
  load(1, id)
}
function goPage(p: number) {
  load(Math.min(Math.max(1, p), totalPages.value), activeCat.value)
  if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
}

const crumbs = [
  { label: 'Accueil', path: '/' },
  { label: 'Informations et actualités', path: PATHS.blog },
]

useHead(() => ({
  ...baseHead({
    title: service.value?.seo?.seo_title || 'Informations, actualités et conseils - Les Ateliers du Terras',
    description:
      service.value?.seo?.seo_description ||
      "Conseils, actualités et réalisations en menuiserie, agencement, couverture et isolation à Mayenne, Laval et La Baule.",
    canonical: PATHS.blog,
    index: service.value?.seo?.robots_index !== false,
  }),
  script: [breadcrumbLd(crumbs)],
}))
</script>

<template>
  <div class="blog">
    <section class="blog-hero">
      <div class="container">
        <span class="eyebrow">Le blog</span>
        <h1>Informations, actualités et conseils</h1>
        <p>
          Retrouvez nos conseils d'artisans, nos réalisations et l'actualité des Ateliers du Terras
          en menuiserie, agencement, couverture et isolation.
        </p>
      </div>
    </section>

    <BreadcrumbBar :crumbs="crumbs" />

    <section class="section">
      <div class="container">
        <div class="filters" role="tablist" aria-label="Filtrer par catégorie">
          <button
            class="chip"
            :class="{ active: activeCat == null }"
            @click="setCat(null)"
          >
            Tous les articles
          </button>
          <button
            v-for="c in cats"
            :key="c.wp_id"
            class="chip"
            :class="{ active: activeCat === c.wp_id }"
            @click="setCat(c.wp_id)"
          >
            {{ c.name }}
          </button>
        </div>

        <p class="count">{{ total }} article{{ total > 1 ? 's' : '' }}</p>

        <div class="grid grid--3" :class="{ 'is-loading': loading }">
          <ArticleCard v-for="a in paged" :key="a.path" :article="a" />
        </div>

        <nav v-if="totalPages > 1" class="pager" aria-label="Pagination">
          <button class="pager__btn" :disabled="page === 1" @click="goPage(page - 1)">
            Précédent
          </button>
          <span class="pager__info">Page {{ page }} sur {{ totalPages }}</span>
          <button class="pager__btn" :disabled="page === totalPages" @click="goPage(page + 1)">
            Suivant
          </button>
        </nav>
      </div>
    </section>
  </div>
</template>

<style scoped>
.blog-hero {
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border);
  padding: 44px 0;
}
.blog-hero p {
  max-width: 720px;
  color: var(--ink);
  margin: 8px 0 0;
}
.filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.chip {
  border: 1px solid var(--border);
  background: #fff;
  color: var(--title);
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.chip:hover {
  border-color: var(--green);
}
.chip.active {
  background: var(--green);
  border-color: var(--green);
  color: #272a23;
}
.is-loading {
  opacity: 0.5;
  transition: opacity 0.15s;
}
.count {
  color: var(--muted);
  font-size: 0.9rem;
  margin: 0 0 18px;
}
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-top: 36px;
}
.pager__btn {
  border: 1px solid var(--green);
  background: #fff;
  color: var(--green-dark);
  padding: 10px 20px;
  border-radius: var(--radius-sm);
  font-weight: 700;
  cursor: pointer;
}
.pager__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pager__info {
  color: var(--muted);
  font-size: 0.9rem;
}
</style>
